/**
 * Which position source is feeding the app, and the device-GNSS health readout.
 *
 * The selection (simulated vs the device's real GPS) lives here; the two feed
 * components watch it and register the matching PositionSource in the gnss
 * store, so the swap rides the existing seam. The device status is surfaced so
 * the UI can show acquiring / live / blocked-by-insecure-context clearly.
 *
 * The chosen source is remembered on its own (like the chart provider and tide
 * source), so a kill/restart comes back on whichever source the skipper left us
 * on rather than resetting every cold start. This is what used to be done by the
 * world-mode restore() step; now that the explicit real/training mode toggle is
 * gone, the source simply persists itself. On a genuinely fresh install (no saved
 * value) we default to the real device GNSS, not the simulator, because the app is
 * for a real boat on real water; the training simulator is a deliberate opt-in.
 * Only an explicitly-saved 'sim' brings the simulator back.
 */
import { create } from 'zustand';
import type { DeviceGnssStatus } from '@/gnss/deviceSource';
import { loadJson, saveJson } from '@/store/persist';

export type PositionSourceKind = 'sim' | 'device';

const POSITION_SOURCE_LS = 'acp.positionSource.v1';
/**
 * Load the saved source. Only an explicitly-saved 'sim' selects the simulator;
 * anything else — no saved value (fresh install) or a corrupted one — falls back
 * to the real device GNSS, so the app opens on real water by default rather than
 * on the training simulator.
 */
export function loadSourceKind(): PositionSourceKind {
  return loadJson<string>(POSITION_SOURCE_LS, 'device') === 'sim' ? 'sim' : 'device';
}

interface PositionSourceState {
  sourceKind: PositionSourceKind;
  setSourceKind: (kind: PositionSourceKind) => void;
  deviceStatus: DeviceGnssStatus;
  deviceMessage: string | null;
  setDeviceStatus: (status: DeviceGnssStatus, message?: string | null) => void;

  /**
   * Health of the REAL device fix, derived once a second by GnssHealthRunner:
   *   live  — a fresh fix within the last few seconds
   *   stale — no fresh fix for a short while (boat greyed, age shown)
   *   lost  — no fix for a good while (a plain warning is raised)
   *   null  — not applicable (the simulator is driving; sim is never "stale")
   * Kept here beside the device status so the whole device-GNSS health story
   * lives in one store. `fixAgeSec` is how old the last fix is, in seconds.
   */
  fixHealth: 'live' | 'stale' | 'lost' | null;
  fixAgeSec: number | null;
  setFixHealth: (health: 'live' | 'stale' | 'lost' | null, ageSec: number | null) => void;
}

export const usePositionSourceStore = create<PositionSourceState>((set) => ({
  sourceKind: loadSourceKind(),
  setSourceKind: (sourceKind) => {
    saveJson(POSITION_SOURCE_LS, sourceKind);
    set({ sourceKind });
  },
  deviceStatus: 'idle',
  deviceMessage: null,
  setDeviceStatus: (deviceStatus, deviceMessage = null) => set({ deviceStatus, deviceMessage }),
  fixHealth: null,
  fixAgeSec: null,
  setFixHealth: (fixHealth, fixAgeSec) => set({ fixHealth, fixAgeSec }),
}));
